// src/SIPClient.js
import { UserAgent, Registerer, Inviter } from "sip.js";

export default class SIPClient {
    constructor(
        { uri, password, wsServer, displayName, onIncomingCall },
        audioRef
    ) {
        this.uri = uri;
        this.password = password;
        this.wsServer = wsServer;
        this.audioRef = audioRef;
        this.displayName = displayName;
        this.onIncomingCall = onIncomingCall; // callback for incoming call

        this.userAgent = new UserAgent({
            uri: UserAgent.makeURI(uri),
            displayName: displayName,
            transportOptions: {
                server: wsServer,
            },
            authorizationUsername: uri.split(":")[1].split("@")[0],
            authorizationPassword: password,
            contactName: uri.split(":")[1].split("@")[0],
        });

        this.registerer = new Registerer(this.userAgent);
        this.session = null;
        this.muted = false;
        this.onSessionStateChange = null; // callback for session state changes
        this.onCallProgress = null; // callback for call progress (ringing, etc)
        this.onLocalStream = null; // callback for local stream ready
        this.incomingInvitation = null; // store incoming invitation
    }

    async start() {
        await this.userAgent.start();
        await this.registerer.register();

        this.userAgent.delegate = {
            onInvite: async (invitation) => {
                this.incomingInvitation = invitation;
                if (this.onIncomingCall) {
                    this.onIncomingCall(invitation);
                }
            },
        };
    }

    async call(targetUri, extraHeaders = []) {
        const target = UserAgent.makeURI(targetUri);
        // Pass extraHeaders to the Inviter constructor
        this.session = new Inviter(this.userAgent, target, {
            extraHeaders: extraHeaders
        });

        // Get user media (microphone)
        try {
            this.localStream = await navigator.mediaDevices.getUserMedia({
                audio: true,
            });
            if (this.onLocalStream) this.onLocalStream(this.localStream);
        } catch (err) {
            console.error("Could not get user media:", err);
            return;
        }

        // Attach local audio tracks to the peer connection before sending INVITE
        this.session.stateChange.addListener((state) => {
            if (this.onSessionStateChange) {
                this.onSessionStateChange(state, this.session);
            }
            if (state === "Establishing" && this.localStream) {
                const pc = this.session.sessionDescriptionHandler.peerConnection;
                this.localStream.getTracks().forEach((track) => {
                    pc.addTrack(track, this.localStream);
                });
            }
            if (state === "Established") {
                this.setupMedia();
            }
        });

        const requestDelegate = {
            onProgress: (response) => {
                if (this.onCallProgress) {
                    this.onCallProgress(response);
                }
            }
        };

        await this.session.invite({ requestDelegate });
    }

    // Duplicate setupMedia removed, handled below.

    async hangup() {
        if (this.session) {
            // Log the session state for debugging
            let state = this.session.state;
            let stateString =
                typeof state === "string"
                    ? state
                    : state?.toString?.() || state?.name || String(state);
            console.log("Session state before hangup:", stateString);

            if (stateString === "Established") {
                try {
                    await this.session.bye();
                } catch (err) {
                    console.warn("Error sending BYE:", err);
                }
            } else if (stateString === "Initial" || stateString === "Establishing") {
                try {
                    if (typeof this.session.cancel === "function") {
                        await this.session.cancel(); // For outgoing call
                    }
                    if (typeof this.session.reject === "function") {
                        await this.session.reject(); // For incoming call
                    }
                } catch (err) {
                    console.warn("Error cancelling/rejecting session:", err);
                }
            } else if (stateString === "Terminated") {
                console.warn("Cannot hang up: session is already terminated.");
            } else {
                console.warn("Cannot hang up: session state is", stateString);
            }
            this.session = null;
        }
        // Clean up local audio stream
        if (this.localStream) {
            this.localStream.getTracks().forEach((track) => track.stop());
            this.localStream = null;
        }
    }

    async stop() {
        await this.registerer.unregister();
        await this.userAgent.stop();
    }

    // Mute/unmute local audio
    setMuted(mute) {
        this.muted = mute;

        // 1. Toggle on the current local stream if it exists (for visualization)
        if (this.localStream) {
            this.localStream.getAudioTracks().forEach((track) => {
                track.enabled = !mute;
            });
        }

        // 2. Also toggle the track in the active session's PeerConnection sender
        // This is critical - it actually mutes the audio being sent over the network
        if (this.session && this.session.sessionDescriptionHandler && this.session.sessionDescriptionHandler.peerConnection) {
            const pc = this.session.sessionDescriptionHandler.peerConnection;
            const senders = pc.getSenders();
            const audioSender = senders.find(s => s.track && s.track.kind === 'audio');
            if (audioSender && audioSender.track) {
                audioSender.track.enabled = !mute;
            }
        }
    }

    // Set output device for remote audio (if supported)
    async setOutputDevice(deviceId) {
        if (
            this.audioRef &&
            this.audioRef.current &&
            this.audioRef.current.setSinkId
        ) {
            try {
                await this.audioRef.current.setSinkId(deviceId);
            } catch (err) {
                console.warn("Failed to set output device:", err);
            }
        }
    }

    // Get SIP session ID (Call-ID header)
    getSessionId() {
        if (this.session && this.session.request) {
            return this.session.request.callId || this.session.request.callID || null;
        }
        return null;
    }

    // Get SIP session status/state
    getSessionStatus() {
        if (this.session) {
            let state = this.session.state;
            return typeof state === "string"
                ? state
                : state?.toString?.() || state?.name || String(state);
        }
        return null;
    }


    // Accept incoming call
    async acceptIncomingCall() {
        if (this.incomingInvitation) {
            this.session = this.incomingInvitation;
            // Check if state is Initial (standard for new incoming invites)
            // sip.js state handling can be tricky, typically "Initial" or "HaveLocalOffer" / "HaveRemoteOffer" depending on exact flow.
            // But "Establishing" usually means it's already in progress.

            const state = this.session.state;

            // Attach state change listener for incoming calls so we detect 'Terminated' (Remote Hangup)
            this.session.stateChange.addListener((newState) => {
                if (this.onSessionStateChange) {
                    this.onSessionStateChange(newState, this.session);
                }
            });

            if (state !== "Initial" && state !== "HaveRemoteOffer") {
                console.warn(`Incoming invitation is in state '${state}', cannot accept again or invalid.`);
                // If it is already establishing/established, we might just want to return or treat it as accepted.
                if (state === "Established" || state === "Establishing") {
                    this.setupMedia();
                    this.incomingInvitation = null;
                    return;
                }
            }

            try {
                await this.session.accept({
                    sessionDescriptionHandlerOptions: {
                        constraints: { audio: true, video: false },
                    },
                });
                this.setupMedia();
            } catch (err) {
                console.error("Failed to accept call:", err);
            }
            this.incomingInvitation = null;
        }
    }

    setupMedia() {
        if (!this.session || !this.session.sessionDescriptionHandler) return;

        const pc = this.session.sessionDescriptionHandler.peerConnection;
        const remoteStream = new MediaStream();

        // 1. Gather existing tracks
        pc.getReceivers().forEach((receiver) => {
            if (receiver.track) {
                remoteStream.addTrack(receiver.track);
            }
        });

        // 2. Listen for future tracks (if negotiation continues)
        pc.ontrack = (event) => {
            if (event.track) {
                remoteStream.addTrack(event.track);
            }
        };

        // 3. Attach to Audio Element
        if (this.audioRef && this.audioRef.current) {
            this.audioRef.current.srcObject = remoteStream;
            // Ensure we try to play
            const attemptPlay = async () => {
                try {
                    await this.audioRef.current.play();
                } catch (e) {
                    console.warn("Audio play failed, retrying on interaction or next tick", e);
                }
            };

            attemptPlay();

            // Also ensure volume is up
            this.audioRef.current.volume = 1.0;
        }

        // Get Local Audio (for incoming calls where we didn't manually getUserMedia)
        if (!this.localStream) {
            const senders = pc.getSenders();
            const localTrack = senders.find((s) => s.track && s.track.kind === "audio")?.track;
            if (localTrack) {
                this.localStream = new MediaStream([localTrack]);
                if (this.onLocalStream) {
                    this.onLocalStream(this.localStream);
                }
            }
        }
    }

    // Reject incoming call
    async rejectIncomingCall() {
        if (this.incomingInvitation) {
            const state = this.incomingInvitation.state;
            if (state !== "Terminated" && state !== "Canceled") {
                try {
                    await this.incomingInvitation.reject();
                } catch (err) {
                    console.warn("Error rejecting call:", err);
                }
            }
            this.incomingInvitation = null;
        }
    }
    sendDTMF(tone) {
        if (!this.session) {
            console.warn("Cannot send DTMF: No active session");
            return;
        }

        const state = this.session.state;
        const stateString =
            typeof state === "string"
                ? state
                : state?.toString?.() || state?.name || String(state);

        console.log(`Sending DTMF: ${tone}, Session State: ${stateString}`);

        // Debugging available methods if needed
        // console.log("Session keys:", Object.keys(this.session));
        // console.log("Session prototype:", Object.getPrototypeOf(this.session));

        // Method 1: Try standard SIP.js session.dtmf (SIP INFO)
        let handled = false;
        if (stateString === "Established") {
            if (typeof this.session.dtmf === "function") {
                console.log("Using session.dtmf (SIP INFO)");
                try {
                    this.session.dtmf(tone);
                    handled = true;
                } catch (e) {
                    console.error("session.dtmf failed", e);
                }
            } else {
                console.warn("session.dtmf function missing");
            }
        }

        // Method 2: Try RTP DTMF (RFC 2833/4733) via PeerConnection
        // This is often required for IVRs even if SIP INFO is supported.
        if (!handled || true) { // Force checking RTP path as it is generally more reliable for IVR
            try {
                const pc = this.session.sessionDescriptionHandler?.peerConnection;
                if (pc) {
                    const senders = pc.getSenders();
                    const audioSender = senders.find(s => s.track && s.track.kind === 'audio');
                    if (audioSender && audioSender.dtmf) {
                        console.log("Sending DTMF via RTP (RFC 2833/4733)");
                        audioSender.dtmf.insertDTMF(tone, 160, 70); // tone, duration ms, interToneGap ms
                        return;
                    } else {
                        console.warn("No audio sender with DTMF support found");
                    }
                } else {
                    console.warn("No PeerConnection found for RTP DTMF");
                }
            } catch (err) {
                console.error("RTP DTMF failed", err);
            }
        }
    }

    // Switch Input Device (Microphone)
    async setInputDevice(deviceId) {
        if (!this.session || this.session.state !== "Established") {
            console.warn("Session not established, cannot switch input device");
            return;
        }

        try {
            const newStream = await navigator.mediaDevices.getUserMedia({
                audio: { deviceId: { exact: deviceId } },
            });

            const newTrack = newStream.getAudioTracks()[0];
            const pc = this.session.sessionDescriptionHandler.peerConnection;
            const sender = pc.getSenders().find((s) => s.track && s.track.kind === "audio");

            if (sender) {
                await sender.replaceTrack(newTrack);

                // Stop old tracks
                if (this.localStream) {
                    this.localStream.getTracks().forEach((t) => t.stop());
                }

                this.localStream = newStream;

                // Re-apply mute state if needed
                if (this.muted) {
                    newTrack.enabled = false;
                }

                // Notify local stream update so UI visualizers work
                if (this.onLocalStream) {
                    this.onLocalStream(newStream);
                }
            }
        } catch (err) {
            console.error("Failed to switch input device:", err);
        }
    }
}
